import styled from 'styled-components';

interface RowProps {
    reverse?: boolean;
}

export const SectionRow = styled.div<RowProps>`
    display: flex;
    flex-direction: ${props => (props.reverse ? 'row-reverse' : 'row')};
    align-items: center;
    padding: calc(100vw * 0.05) 0;
    justify-content: center;
    gap: 30px;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 24px;
    }
`;

export const SectionContent = styled.div`
    padding: 0 44px;

    h2 {
        margin-bottom: 24px;
        color: var(--ink-inverse);
        font-family: var(--font-heading);
    }

    p {
        color: rgba(255, 255, 255, 0.72);
        font-size: var(--text-body);
        line-height: var(--leading-body);
        margin-bottom: 0;
    }

    @media (max-width: 768px) {
        padding-left: 0;
        padding-right: 0;
        text-align: center;
    }
`;

export const SectionImg = styled.div`
    flex-shrink: 0;
    max-width: 45%;

    img {
        max-width: 100%;
        border-radius: 15px;
    }

    @media (max-width: 768px) {
        max-width: 100%;
    }
`;

export const BottomActionRow = styled.div`
    background-color: var(--brand);
    text-align: center;
    color: var(--ink-inverse);
    padding: calc(100vw * 0.05) var(--page-gutter);

    h3 {
        margin-bottom: 24px;
        font-family: var(--font-display);
        font-size: 48px;
        line-height: 1;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: var(--ink-inverse);
    }

    button {
        background: var(--surface-0);
        color: var(--ink-inverse);
        box-shadow: 0 10px 24px rgba(0, 0, 0, 0.15);

        &:hover {
            background: var(--surface-2);
            border-color: transparent;
        }
    }
`;

export const HomepageWrapper = styled.div`
    background-color: var(--surface-0);
    color: var(--ink-inverse);
`;

export const HomepageInner = styled.div`
    background-color: var(--surface-0);
`;

export const HeroSection = styled.section`
    position: relative;
    z-index: 2;
    background-color: #ffffff;
    padding: 12px 12px;

    @media (max-width: 768px) {
        padding: 8px 8px;
    }
`;

export const HeroCard = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: min(70vh, 640px);
    padding: calc(100vw * 0.12) 40px;
    border-radius: 15px;
    overflow: visible;
    background-color: #111111;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: url('/images/hero-img.webp') center center / cover no-repeat;
        pointer-events: none;
        opacity: 0.8;
    }

    @media (max-width: 768px) {
        min-height: 480px;
        padding: 48px 20px;
        border-radius: 12px;
    }
`;

export const HeroContent = styled.div`
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 980px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    h1 {
        font-family: var(--font-display);
        font-size: 72px;
        font-weight: 400;
        line-height: 70px;
        letter-spacing: 1px;
        text-transform: uppercase;
        margin: 0 0 28px;
        color: #ffffff;
    }

    > div {
        width: 100%;
        max-width: 640px;
        position: relative;
        z-index: 3;
    }

    .autocomplete-address-row {
        justify-content: center;
        position: relative;
        z-index: 3;
    }

    @media (max-width: 1300px) {
        h1 {
            font-size: 58px;
            line-height: 58px;
        }
    }

    @media (max-width: 999px) {
        h1 {
            font-size: 44px;
            line-height: 46px;
        }
    }

    @media (max-width: 767px) {
        h1 {
            font-size: 32px;
            line-height: 34px;
            letter-spacing: 0.5px;
        }
    }

    @media (max-width: 400px) {
        h1 {
            font-size: 24px;
            line-height: 26px;
            letter-spacing: 0.4px;
        }
    }
`;

export const HeroHighlight = styled.span`
    color: var(--brand);
    white-space: nowrap;
`;

export const IntroSection = styled.section`
    padding: 72px 52px 88px;
    background-color: var(--surface-0);

    @media (max-width: 999px) {
        padding: 56px 32px 64px;
    }

    @media (max-width: 767px) {
        padding: 40px 20px 48px;
    }
`;

export const IntroInner = styled.div`
    display: grid;
    grid-template-columns: minmax(240px, 0.85fr) minmax(0, 1.15fr);
    align-items: start;
    column-gap: 64px;
    row-gap: 24px;
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;

    h2 {
        margin: 0;
        font-family: var(--font-display);
        font-size: 64px;
        font-weight: 400;
        line-height: 0.92;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: #ffffff;
    }

    p {
        margin: 0;
        max-width: 36em;
        text-align: left;
        font-family: var(--font-body);
        font-size: var(--text-body);
        line-height: var(--leading-body);
        color: rgba(255, 255, 255, 0.88);
    }

    @media (max-width: 999px) {
        grid-template-columns: 1fr;
        max-width: 720px;

        h2 {
            font-size: 52px;
        }
    }

    @media (max-width: 767px) {
        h2 {
            font-size: 42px;
        }

        p {
            font-size: 16px;
            line-height: 24px;
        }
    }
`;

export const HeroFeatures = styled.ul`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 12px 36px;
    list-style: none;
    margin: 0 0 32px;
    padding: 0;
    width: 100%;

    li {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #ffffff;
        font-family: var(--font-body);
        font-size: 16px;
        line-height: 1.3;
        white-space: nowrap;

        .feature-step {
            flex-shrink: 0;
            color: var(--brand);
            font-family: var(--font-display);
            font-size: 22px;
            font-weight: 400;
            line-height: 1;
            letter-spacing: 0.5px;
        }
    }

    @media (max-width: 767px) {
        gap: 10px 20px;
        margin-bottom: 24px;

        li {
            font-size: 14px;
            white-space: normal;

            .feature-step {
                font-size: 20px;
            }
        }
    }
`;

export const VideoPopupWrapper = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
`;

export const VideoPopupContainer = styled.div`
    transform: translateY(-50%);
    position: relative;
    top: 50%;
    overflow: hidden;
    max-width: 850px;
    max-height: 500px;
    margin: 0 auto;
    z-index: 99;
`;

export const VideoPopupInner = styled.div`
    padding-bottom: 56.25%;

    iframe {
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
    }
`;

export const VideoPopupCloseBtn = styled.button`
    display: inline-block;
    position: absolute;
    top: 16px;
    right: 10px;
    border: 0 none;
    background: transparent;
    cursor: pointer;
    z-index: 100;

    svg {
        color: #aaa;
        font-size: 36px;
    }
`;

export const VideoPopupMask = styled.div`
    position: absolute;
    background-color: rgba(0, 0, 0, 0.8);
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
`;
